// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import React, { useState, ChangeEvent, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useFlexFlagIsOn, useFlexFeatureFlagsContext } from '@indeed/flex-feature-flags'

import * as authActions from 'syft-acp-core/actions/auth'
import * as appActions from 'syft-acp-core/actions/app'
import LoginSSOFormPure from './LoginSSOFormPure'
import LoginSSORedirect from './LoginSSORedirect'
import LoginTroubleModal from './LoginTroubleModal'
import { validateEmailAndDomain } from './helpers'

import { LoginSSOFormComponentProps as Props } from './LoginSSOForm.types'

// GrowthBook flag (runtime-controllable). Must be tagged for anonymous evaluation so it resolves on
// the pre-auth login page. When on: skip the email form and redirect straight to Okta (STSMPD-1639).
export const ACP_SSO_EMAILLESS_FLAG = 'stsmpd_1639_acp_sso_emailless'

const LoginSSOFormComponent = ({
  errorBody,
  isCheckingLogin,
  isLoggedIn,
  hasRehydrated,
  actions,
  query,
}: Props) => {
  const isEmaillessEnabled = useFlexFlagIsOn(ACP_SSO_EMAILLESS_FLAG)
  const { isLoading: areFlagsLoading } = useFlexFeatureFlagsContext()

  const [emailValidationMessage, setEmailValidationMessage] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [openTroubleModal, setOpenTroubleModal] = useState(false)

  // Redeem the SSO code when Okta returns us here with one (both flows).
  useEffect(() => {
    if (query?.code) {
      actions.consumeIndeedToken(query?.code)
    }
  }, [])

  useEffect(() => {
    setEmailValidationMessage(null)
  }, [isCheckingLogin])

  // Emailless auto-login: once the flag resolves on and rehydration has settled (logged-out, not
  // mid-redeem), redirect straight to Okta; identity comes from the SAML NameID at /consume.
  useEffect(() => {
    if (isEmaillessEnabled && hasRehydrated && !isLoggedIn && !query?.code) {
      actions.beginLoginSSOEmailless()
    }
  }, [isEmaillessEnabled, hasRehydrated, isLoggedIn, query?.code])

  const typeEmail = (ev: ChangeEvent<HTMLInputElement>) => {
    setEmail(ev.target.value)
  }

  const closeLoginModal = () => {
    setOpenTroubleModal(false)
  }

  const loginReq = (ev: ChangeEvent<HTMLInputElement>) => {
    ev.preventDefault()
    if (validateEmailAndDomain(email)) {
      setEmailValidationMessage(null)
      actions.beginLoginSSO(email)
    }
    setEmailValidationMessage(
      'Invalid email domain. Please login with your Indeed Flex or Syftapp email address or raise an IT support ticket on ',
    )
  }

  const loginTrouble = () => {
    setOpenTroubleModal(true)
  }

  const message = errorBody?.error_description
  const troubleModal = (
    <LoginTroubleModal
      handleSubmit={actions.resetAndReload}
      isOpen={openTroubleModal}
      handleOnExit={closeLoginModal}
    />
  )

  // Wait for the flag to resolve before rendering: while it loads the flag reads false, so a flag-on
  // admin would briefly see the email form before it flips to the SSO redirect.
  if (areFlagsLoading) {
    return null
  }

  if (isEmaillessEnabled) {
    // On the happy path this is a brief "redirecting to Okta" spinner. A failed sign-in (e.g. token
    // redeem rejected) leaves a message with isCheckingLogin=false → show a retry instead.
    const isRetry = !!message && !isCheckingLogin

    return (
      <>
        <LoginSSORedirect
          isRetry={isRetry}
          feedbackMessage={message}
          onRetry={actions.beginLoginSSOEmailless}
          onLoginTrouble={loginTrouble}
        />
        {troubleModal}
      </>
    )
  }

  return (
    <>
      <LoginSSOFormPure
        isDisabled={isCheckingLogin}
        onTypeEmail={typeEmail}
        valueEmail={email}
        onSubmit={loginReq}
        onLoginTrouble={loginTrouble}
        feedbackMessage={message}
        emailValidationMessage={emailValidationMessage}
        isAwaitingRehydration={!hasRehydrated}
      />
      {troubleModal}
    </>
  )
}

const LoginSSOForm = connect(
  state => ({
    feedbackMessage: state.auth?.lastMessage,
    errorBody: state.auth?.errorBody,
    hasRehydrated: state.localstorage?.hasRehydrated,
    isCheckingLogin: state.auth?.isCheckingLogin,
    isLoggedIn: state.auth?.isLoggedIn,
    query: state.routing?.locationBeforeTransitions.query,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        ...authActions,
        ...appActions,
      },
      dispatch,
    ),
  }),
)(LoginSSOFormComponent)

export { LoginSSOForm }
